import { Space } from 'antd';
import { ReactNode, useState, useMemo } from 'react';
import config from 'config/config';
import { ICS2Notification, ICS2NotificationError } from 'store/declarations/eu/ics2/ics2-notification';
import useIcs2FunctionalErrorLabel from 'hooks/useIcs2FunctionalErrorLabel';
import {
    parseIcs2EnsMessageElementPath,
    parseIcs2MessageElementPath,
} from 'views/declarations/eu/ics2/errors/parseMessageElementPath';
import useGoToIcs2ErrorLocation from 'views/declarations/eu/ics2/errors/useGoToIcs2ErrorLocation';
import { compareIcs2ErrorLocations } from 'views/declarations/eu/ics2/errors/sortLocations';
import { formatIcs2ErrorLocation } from 'views/declarations/eu/ics2/errors/formatLocation';
import NotificationsList from '../../declaration-notifications/ics2/components/NotificationsList';
import { BackToList, ShowRawDataDiv, StickyDrawerHeader, Title } from '../../DeclarationsNotificationsDrawer.styles';
import RawMessage from '../../RawMessage';
import DeclarationErrorCards from '../error-cards/DeclarationErrorCards';
import getIcs2ErrorSections from './getIcs2ErrorSections';
import Ics2ErrorDetails from './Ics2ErrorDetails';

type Props = {
    errors?: ICS2NotificationError[];
    latestNotification?: ICS2Notification;
    showRawData?: boolean;
    nextSteps?: ReactNode;
    onClose?: () => void;
    showErrorCards?: boolean;
};

const Ics2DeclarationErrors = ({
    errors,
    latestNotification,
    showRawData = false,
    nextSteps,
    onClose,
    showErrorCards = false,
}: Props) => {
    const getIcs2FunctionalErrorLabel = useIcs2FunctionalErrorLabel();
    const goToErrorLocation = useGoToIcs2ErrorLocation(onClose);
    const [selectedError, setSelectedError] = useState<ICS2NotificationError | undefined>(undefined);
    const [showRawMessage, setShowRawMessage] = useState<boolean>(showRawData);

    /**
     * Customs reports errors in whatever order it validated them, which leaves the reader guessing
     * at which item each one belongs to. Resolving the pointer lets us both name the level and put
     * the list in the order the declaration reads: master first, then its items, then each house
     * consignment with its own. The sort is stable, so errors on the same level keep the order
     * customs sent them in, and ones whose pointer we cannot resolve fall to the bottom unchanged.
     */
    const locatedErrors = useMemo(
        () =>
            (errors ?? [])
                .map((error) => ({ error, location: parseIcs2MessageElementPath(error.pointer?.messageElementPath) }))
                .sort((a, b) => compareIcs2ErrorLocations(a.location, b.location)),
        [errors]
    );

    const sections = useMemo(
        () => getIcs2ErrorSections(errors, getIcs2FunctionalErrorLabel),
        [errors, getIcs2FunctionalErrorLabel]
    );

    const parseLocation = showErrorCards ? parseIcs2EnsMessageElementPath : parseIcs2MessageElementPath;

    if (showRawMessage)
        return (
            <>
                {!showRawData && (
                    <StickyDrawerHeader>
                        <BackToList onClick={() => setShowRawMessage(false)}>Back to list</BackToList>
                    </StickyDrawerHeader>
                )}
                <Title>Error details</Title>
                <RawMessage>{latestNotification?.rawMessage}</RawMessage>
            </>
        );

    if (selectedError)
        return (
            <Ics2ErrorDetails
                error={selectedError}
                location={parseLocation(selectedError.pointer?.messageElementPath)}
                notification={latestNotification}
                onBack={() => setSelectedError(undefined)}
                nextSteps={nextSteps}
                onClose={onClose}
            />
        );

    return (
        <>
            {config.isPIT && (
                <ShowRawDataDiv>
                    <span onClick={() => setShowRawMessage(true)}>Show raw data</span>
                </ShowRawDataDiv>
            )}

            {showErrorCards ? (
                <DeclarationErrorCards
                    sections={sections}
                    onGoToField={(_field, card) =>
                        goToErrorLocation(parseLocation(card.error.pointer?.messageElementPath))
                    }
                    onShowDetails={(error) => setSelectedError(error)}
                />
            ) : (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    {locatedErrors.map(({ error, location }, index) => (
                        <NotificationsList
                            key={`${error.validationCode ?? 'error'}-${index}`}
                            code={error.validationCode ?? '-'}
                            description={
                                getIcs2FunctionalErrorLabel(error.validationCode) ??
                                error.description ??
                                'Unknown error'
                            }
                            location={formatIcs2ErrorLocation(location)}
                            severity="danger"
                            onSeeDetails={() => setSelectedError(error)}
                        />
                    ))}
                </Space>
            )}
        </>
    );
};

export default Ics2DeclarationErrors;
